import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BadgeCheck, LogOut, MessageSquarePlus, Send, Sparkles,
} from 'lucide-react';
import { api, unwrap, unwrapPaged } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLocale } from '@/i18n/locale-provider';
import { LanguageToggle } from '@/components/language-toggle';
import { NuatMark, NuatWordmark, Spinner } from '@/components/ui';
import { SourcePanel } from '@/components/source-panel';
import { MessageBubble } from '@/components/message-bubble';
import type { ChatMessage, Conversation } from '@/lib/types';

export function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const { user, signOut } = useAuth();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<ChatMessage | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const endRef = useRef<HTMLDivElement>(null);

  const loadConversations = useCallback(async () => {
    try {
      const page = await unwrapPaged<Conversation>(api.ai.get('/chat/conversations?pageSize=40'));
      setConversations(page.items);
    } catch {
      // A failed sidebar must not block the conversation itself.
    }
  }, []);

  useEffect(() => { void loadConversations(); }, [loadConversations]);

  useEffect(() => {
    unwrap<{ suggestions: string[] }>(api.ai.get(`/chat/suggestions?locale=${locale}`))
      .then((data) => setSuggestions(data.suggestions.slice(0, 6)))
      .catch(() => setSuggestions([]));
  }, [locale]);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingThread(true);
    unwrap<Conversation & { messages: ChatMessage[] }>(api.ai.get(`/chat/conversations/${conversationId}`))
      .then((data) => { if (!cancelled) setMessages(data.messages ?? []); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? t.chat.loadFailed); })
      .finally(() => { if (!cancelled) setLoadingThread(false); });
    return () => { cancelled = true; };
  }, [conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  async function ask(question: string) {
    if (!question.trim() || sending) return;
    setSending(true);
    setError(null);
    setInput('');

    try {
      let id = conversationId;
      if (!id) {
        const created = await unwrap<Conversation>(
          api.ai.post('/chat/conversations', { title: question.slice(0, 120), locale }),
        );
        id = created.id;
        navigate(`/c/${id}`, { replace: true });
        void loadConversations();
      }

      // Optimistically show the question so the thread does not sit empty while the
      // retrieval and model call run.
      setMessages((current) => [
        ...current,
        { id: `pending-${Date.now()}`, role: 'user', content: question, createdAt: new Date().toISOString() } as ChatMessage,
      ]);

      const answer = await unwrap<ChatMessage>(
        api.ai.post(`/chat/conversations/${id}/messages`, { content: question }),
      );

      // Re-read the thread so the optimistic message is replaced by the stored one.
      const thread = await unwrap<Conversation & { messages: ChatMessage[] }>(
        api.ai.get(`/chat/conversations/${id}`),
      );
      setMessages(thread.messages ?? []);
      if (answer.sources && (answer.sources as unknown[]).length) setInspecting(answer);
      void loadConversations();
    } catch (err) {
      setError((err as { message?: string }).message ?? t.chat.answerFailed);
      // Drop the optimistic bubble; the question was not recorded.
      setMessages((current) => current.filter((m) => !m.id.startsWith('pending-')));
    } finally {
      setSending(false);
    }
  }

  async function rate(message: ChatMessage, rating: 'HELPFUL' | 'NOT_HELPFUL') {
    try {
      await api.ai.post(`/chat/messages/${message.id}/feedback`, { rating });
      setMessages((current) =>
        current.map((m) => (m.id === message.id ? { ...m, feedbackGiven: rating } : m)),
      );
    } catch {
      // Feedback is best-effort.
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ------------------------------------------------------- conversations */}
      <aside className="hidden w-64 shrink-0 flex-col border-e border-deep-800 bg-deep-900 lg:flex">
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-deep-800 px-5">
          <NuatMark size={28} tone="inverse" />
          <span className="flex flex-col leading-none">
            <NuatWordmark height={12} className="text-white" />
            <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-glow-400">
              {t.app.suite}
            </span>
          </span>
        </div>

        <div className="p-3">
          <button
            type="button"
            onClick={() => { navigate('/'); setMessages([]); setInspecting(null); }}
            className="flex w-full items-center gap-2 rounded-md bg-deep-700 px-3 py-2 text-sm font-medium text-white hover:bg-deep-600"
          >
            <MessageSquarePlus size={16} aria-hidden />
            {t.chat.newConversation}
          </button>
        </div>

        <nav aria-label={t.chat.conversations} className="flex-1 overflow-y-auto px-3 pb-3">
          <ul className="space-y-0.5">
            {conversations.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => { navigate(`/c/${conversation.id}`); setInspecting(null); }}
                  className={`w-full truncate rounded-md px-3 py-2 text-start text-sm transition-colors ${
                    conversation.id === conversationId
                      ? 'bg-deep-700 text-white'
                      : 'text-mist-400 hover:bg-deep-800 hover:text-white'
                  }`}
                  title={conversation.title}
                >
                  {conversation.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-deep-800 p-3">
          <div className="rounded-md bg-deep-800 px-3 py-2">
            <p className="truncate text-sm font-medium text-white">{user?.fullName}</p>
            <p className="ltr-embed truncate text-xs text-mist-500">{user?.email}</p>
          </div>
          <div className="mt-2">
            <LanguageToggle tone="dark" />
          </div>
          <button
            type="button"
            onClick={signOut}
            className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-mist-400 hover:bg-deep-800 hover:text-white"
          >
            <LogOut size={15} aria-hidden /> {t.app.signOut}
          </button>
        </div>
      </aside>

      {/* -------------------------------------------------------- conversation */}
      <div className="flex min-w-0 flex-1 flex-col bg-white">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-mist-200 px-5">
          <NuatMark size={26} className="lg:hidden" />
          <div className="min-w-0">
            <h1 className="truncate font-display text-base font-semibold text-deep-700">
              {t.chat.heading}
            </h1>
            <p className="text-xs text-mist-500">{t.chat.strapline}</p>
          </div>
          <div className="ms-auto lg:hidden">
            <LanguageToggle />
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
          {loadingThread ? (
            <Spinner label={t.chat.loadingThread} />
          ) : messages.length === 0 ? (
            <div className="mx-auto max-w-2xl py-10 text-center">
              <NuatMark size={56} className="mx-auto" />
              <h2 className="mt-6 font-display text-2xl font-semibold text-deep-700">
                {t.chat.emptyTitle}
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-mist-600">{t.chat.emptyBody}</p>

              <div className="mt-8 grid gap-2 sm:grid-cols-2">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => ask(suggestion)}
                    className="rounded-lg border border-mist-200 px-4 py-3 text-start text-sm text-mist-700
                               transition-colors hover:border-deep-300 hover:bg-deep-50"
                  >
                    <Sparkles size={13} className="me-1.5 inline text-glow-600" aria-hidden />
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-5">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onInspect={() => setInspecting(message)}
                  onRate={rate}
                  isInspecting={inspecting?.id === message.id}
                />
              ))}
              {sending && (
                <div className="flex items-center gap-2.5 text-sm text-mist-500">
                  <NuatMark size={22} />
                  <span className="animate-pulse">{t.chat.thinking}</span>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {error && (
          <div className="mx-5 mb-3 rounded-md border border-danger-border bg-danger-bg px-4 py-2.5 text-sm text-danger-fg" role="alert">
            {error}
          </div>
        )}

        <form
          onSubmit={(event) => { event.preventDefault(); ask(input); }}
          className="shrink-0 border-t border-mist-200 px-5 py-4"
        >
          <div className="mx-auto flex max-w-3xl gap-2">
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t.chat.placeholder}
              aria-label={t.chat.inputLabel}
              disabled={sending}
              className="input"
            />
            <button type="submit" disabled={sending || !input.trim()} className="btn-primary shrink-0">
              <Send size={16} aria-hidden />
              <span className="hidden sm:inline">{t.chat.ask}</span>
            </button>
          </div>
          <p className="mx-auto mt-2 flex max-w-3xl items-center gap-1.5 text-xs text-mist-500">
            <BadgeCheck size={12} aria-hidden />
            {t.chat.grounding}
          </p>
        </form>
      </div>

      {/* --------------------------------------------------------- source pane */}
      {inspecting && (
        <SourcePanel message={inspecting} onClose={() => setInspecting(null)} />
      )}
    </div>
  );
}
